import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useExtraT } from "@/lib/extraI18n";
import { VISIBLE_SERVICES as SERVICES, CANTONS_SEO } from "@/lib/seo";
import { SERVICE_NAMES_I18N } from "@/lib/serviceContent";
import { CITIES } from "@/lib/citiesData";

const FOOTER_HEADINGS = {
  fr: { services: "Services", zones: "Zones", cities: "Villes desservies", tarifs: "Tarifs", guides: "Guides", outils: "Outils", realisations: "Réalisations", pro: "Professionnels" },
  de: { services: "Leistungen", zones: "Gebiete", cities: "Bediente Städte", tarifs: "Preise", guides: "Ratgeber", outils: "Tools", realisations: "Referenzen", pro: "Geschäftskunden" },
  en: { services: "Services", zones: "Areas", cities: "Cities we serve", tarifs: "Pricing", guides: "Guides", outils: "Tools", realisations: "Case studies", pro: "Businesses" },
};

export default function Footer() {
  const { t, lang } = useI18n();
  const x = useExtraT();
  const h = FOOTER_HEADINGS[lang] || FOOTER_HEADINGS.fr;
  const serviceNames = SERVICE_NAMES_I18N[lang] || SERVICE_NAMES_I18N.fr;
  return (
    <footer className="bg-white border-t border-gray-200 mt-20 relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 lg:gap-x-10 gap-y-10 relative z-10">
        <div className="sm:col-span-2 md:col-span-3 lg:col-span-1">
          <img
            src="/branding/logo-blue.png"
            alt="Mopper"
            className="h-12 w-auto object-contain"
            loading="lazy"
            decoding="async"
          />
          <p className="mt-4 text-[#4B5563] text-sm leading-relaxed max-w-xs">{t.footer.tagline}</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#9CA3AF] font-bold mb-3"><Link to="/services" className="hover:text-[#2D5BFF]" data-testid="footer-services-title">{h.services}</Link></div>
          <ul className="space-y-2 text-sm text-[#0A0F2C]">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-[#2D5BFF]" data-testid={`footer-service-${s.slug}`}>{serviceNames[s.key] || s.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#9CA3AF] font-bold mb-3"><Link to="/zones" className="hover:text-[#2D5BFF]" data-testid="footer-zones-title">{h.zones}</Link></div>
          <ul className="space-y-2 text-sm text-[#0A0F2C]">
            {CANTONS_SEO.map((c) => (
              <li key={c.slug}>
                <Link to={`/zones/${c.slug}`} className="hover:text-[#2D5BFF]" data-testid={`footer-zone-${c.slug}`}>{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#9CA3AF] font-bold mb-3">{t.footer.col1}</div>
          <ul className="space-y-2 text-sm text-[#0A0F2C]">
            <li><Link to="/a-propos" className="hover:text-[#2D5BFF]">{t.nav.about}</Link></li>
            <li><Link to="/tarifs" className="hover:text-[#2D5BFF]" data-testid="footer-tarifs-link">{h.tarifs}</Link></li>
            <li><Link to="/guides" className="hover:text-[#2D5BFF]" data-testid="footer-guides-link">{h.guides}</Link></li>
            <li><Link to="/outils" className="hover:text-[#2D5BFF]" data-testid="footer-outils-link">{h.outils}</Link></li>
            <li><Link to="/realisations" className="hover:text-[#2D5BFF]" data-testid="footer-realisations-link">{h.realisations}</Link></li>
            <li><Link to="/professionnels" className="hover:text-[#2D5BFF]" data-testid="footer-pro-link">{h.pro}</Link></li>
            <li><Link to="/carriere" className="hover:text-[#2D5BFF]">{t.nav.careers}</Link></li>
            <li><Link to="/aide" className="hover:text-[#2D5BFF]">{t.nav.help}</Link></li>
            <li><Link to="/devis" className="hover:text-[#2D5BFF]">{t.cta.book}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#9CA3AF] font-bold mb-3">{t.footer.col3}</div>
          <ul className="space-y-2 text-sm text-[#0A0F2C]">
            <li><a href="mailto:hello@gomopper.ch" className="hover:text-[#2D5BFF]">hello@gomopper.ch</a></li>
            <li><a href="https://wa.me/41782321850" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-[#2D5BFF]">WhatsApp +41 78 232 18 50</a></li>
            <li className="text-[#4B5563]">{x.common.lausanneGeneveSuisse}</li>
          </ul>
        </div>
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-12 md:-bottom-20 left-0 right-0 text-center select-none"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: "clamp(120px, 22vw, 320px)",
          letterSpacing: "-0.05em",
          lineHeight: 1,
          color: "rgba(45,91,255,0.05)",
        }}
      >
        mopper.
      </div>
      {/* Villes desservies — bloc compact style Batmaid, discret & SEO-friendly */}
      <div className="border-t border-gray-100 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="footer-cities-block">
          <div className="text-[11px] uppercase tracking-widest text-[#9CA3AF] font-bold mb-2">{h.cities}</div>
          <p className="text-[13px] leading-relaxed text-[#4B5563]">
            {CITIES.map((c, i) => (
              <span key={c.slug}>
                <Link to={`/villes/${c.slug}`} className="hover:text-[#2D5BFF] transition-colors" data-testid={`footer-city-${c.slug}`}>{c.name}</Link>
                {i < CITIES.length - 1 ? ", " : "."}
              </span>
            ))}
          </p>
        </div>
      </div>
      <div className="border-t border-gray-100 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between text-xs text-[#9CA3AF]">
          <div>{t.footer.rights}</div>
          <div className="flex gap-6 mt-2 md:mt-0">
            <Link to="/aide" className="hover:text-[#0A0F2C]">{t.nav.help}</Link>
            <Link to="/admin" className="hover:text-[#0A0F2C]" data-testid="footer-admin-link">{x.common.adminLink}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
